import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  RefreshCw,
  Signal,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'
import {
  KpiGrid,
  RevenueChart,
  SecurityPosture,
  BackupIntelligence,
  HealthGrid,
  UptimeHeatmap,
  HealthScoreGauge,
} from './components'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardMetrics {
  mrr: number
  churnRate: number
  activeTickets: number
  ticketVelocity: number
  slaCompliance: number
  criticalAlerts: number
  healthScore: number
  revenueTrend: { timestamp: string; value: number }[]
  security: {
    threatLevel: string
    riskScore: number
    vulnerabilities: { critical: number; high: number; medium: number }
  }
  backup: {
    successRate: number
    rpoStatus: string
    totalDataProtected: string
    jobs: { id: string; name: string; status: string; timestamp: string }[]
  }
  uptimeHistory: { date: string; score: number }[]
  healthGrid: { service: string; status: 'healthy' | 'warning' | 'critical'; uptime: string }[]
  licenseUtilization?: {
    used: number
    total: number
    complianceStatus: string
  }
}

// ---------------------------------------------------------------------------
// Mock data (fallback when API endpoint is unavailable)
// ---------------------------------------------------------------------------

const MOCK_METRICS: DashboardMetrics = {
  mrr: 847500,
  churnRate: 2.1,
  activeTickets: 47,
  ticketVelocity: 8.3,
  slaCompliance: 99.2,
  criticalAlerts: 0,
  healthScore: 94,
  revenueTrend: Array.from({ length: 12 }, (_, i) => ({
    timestamp: new Date(2025, i, 1).toISOString(),
    value: 750000 + Math.random() * 150000,
  })),
  security: {
    threatLevel: 'low',
    riskScore: 18,
    vulnerabilities: { critical: 0, high: 3, medium: 12 },
  },
  backup: {
    successRate: 99.7,
    rpoStatus: 'compliant',
    totalDataProtected: '47.2 TB',
    jobs: [
      { id: '1', name: 'SQL-PROD-01 Full', status: 'completed', timestamp: new Date().toISOString() },
      { id: '2', name: 'Exchange-Online', status: 'running', timestamp: new Date().toISOString() },
      { id: '3', name: 'SharePoint-Sites', status: 'completed', timestamp: new Date(Date.now() - 3600000).toISOString() },
    ],
  },
  uptimeHistory: Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 86400000).toISOString(),
    score: 99 + Math.random() * 1,
  })),
  healthGrid: [
    { service: 'Microsoft 365', status: 'healthy', uptime: '99.99%' },
    { service: 'Azure AD', status: 'healthy', uptime: '99.98%' },
    { service: 'ConnectWise', status: 'healthy', uptime: '99.95%' },
    { service: 'NinjaOne RMM', status: 'warning', uptime: '99.82%' },
    { service: 'Datto BCDR', status: 'healthy', uptime: '99.97%' },
    { service: 'SentinelOne', status: 'healthy', uptime: '99.99%' },
  ],
  licenseUtilization: {
    used: 1247,
    total: 1500,
    complianceStatus: 'compliant',
  },
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="p-6 bg-slate-900/50 border-slate-800">
            <div className="space-y-4">
              <div className="flex justify-between">
                <Skeleton className="size-10 rounded-lg" />
                <Skeleton className="h-6 w-14 rounded-full" />
              </div>
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-24" />
            </div>
          </Card>
        ))}
      </div>

      {/* Health gauge + Revenue chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-slate-900/50 border-slate-800">
          <Skeleton className="h-[130px] w-full rounded-xl" />
          <Skeleton className="h-4 w-32 mx-auto mt-4" />
        </Card>
        <div className="lg:col-span-3">
          <Skeleton className="h-[350px] w-full rounded-xl" />
        </div>
      </div>

      {/* Bottom grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-6 bg-slate-900/50 border-slate-800">
            <Skeleton className="h-6 w-40 mb-4" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </Card>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function DashboardError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-6 max-w-md">
        <div className="mx-auto size-16 rounded-2xl bg-rose-500/10 flex items-center justify-center">
          <WifiOff className="size-8 text-rose-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Connection Error</h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            Unable to reach the dashboard API. The system will automatically fall back to
            cached data when available.
          </p>
          <p className="text-xs text-slate-500 mt-2 font-mono">{error.message}</p>
        </div>
        <Button
          variant="outline"
          onClick={onRetry}
          className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <RefreshCw className="size-4 mr-2" />
          Retry Connection
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Critical alert banner
// ---------------------------------------------------------------------------

function CriticalAlertBanner({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <div className="relative overflow-hidden rounded-xl border border-rose-500/30 bg-rose-950/30 backdrop-blur-sm p-4">
      <div className="absolute inset-0 bg-gradient-to-r from-rose-500/5 via-transparent to-rose-500/5" />
      <div className="relative flex items-center gap-4">
        <div className="flex-shrink-0 size-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
          <AlertTriangle className="size-5 text-rose-400 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-rose-300 uppercase tracking-wider">
            Critical Alert{count > 1 ? 's' : ''}
          </h3>
          <p className="text-xs text-rose-400/80 mt-0.5">
            {count} P1 incident{count > 1 ? 's' : ''} requiring immediate attention
          </p>
        </div>
        <div className="flex-shrink-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/20 text-rose-300 text-xs font-bold font-mono">
            <span className="size-2 rounded-full bg-rose-400 animate-ping" />
            {count} ACTIVE
          </span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// License utilization (optional section)
// ---------------------------------------------------------------------------

function LicenseUtilizationBar({
  data,
}: {
  data: { used: number; total: number; complianceStatus: string }
}) {
  const pct = Math.round((data.used / data.total) * 100)
  const isWarning = pct >= 85
  const isCritical = pct >= 95

  return (
    <Card className="p-6 bg-slate-900/50 border-slate-800 backdrop-blur-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white">License Utilization</h3>
          <p className="text-xs text-slate-500 mt-1">Seat allocation & compliance</p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
            data.complianceStatus === 'compliant'
              ? 'bg-emerald-500/10 text-emerald-400'
              : 'bg-rose-500/10 text-rose-400'
          }`}
        >
          {data.complianceStatus}
        </span>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">
            {data.used.toLocaleString()} / {data.total.toLocaleString()} seats
          </span>
          <span
            className={`font-mono font-bold ${
              isCritical
                ? 'text-rose-400'
                : isWarning
                  ? 'text-amber-400'
                  : 'text-emerald-400'
            }`}
          >
            {pct}%
          </span>
        </div>
        <div className="w-full bg-slate-800 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ${
              isCritical
                ? 'bg-rose-500'
                : isWarning
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
            }`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const queryClient = useQueryClient()

  const {
    data: metricsResponse,
    isLoading,
    isError,
    error,
    isFetching,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: () => apiClient.get<DashboardMetrics>('/dashboard/metrics'),
    refetchInterval: 30000,
    retry: 1,
  })

  // Gracefully fall back to mock data when the API is unavailable
  const metrics: DashboardMetrics = metricsResponse?.data ?? MOCK_METRICS
  const isUsingMockData = !metricsResponse?.data

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] })
  }

  // Hard error state (only when we have no data at all and the query failed)
  if (isError && !metricsResponse?.data) {
    return (
      <div className="space-y-6 page-enter">
        <DashboardError error={error as Error} onRetry={handleRefresh} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6 page-enter">
        <DashboardSkeleton />
      </div>
    )
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : null

  return (
    <div className="space-y-8 page-enter">
      {/* ----------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              Executive Command
            </h1>
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                isUsingMockData
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-emerald-500/10 text-emerald-400'
              }`}
            >
              {isUsingMockData ? (
                <>
                  <WifiOff className="size-3" />
                  Demo
                </>
              ) : (
                <>
                  <Wifi className="size-3" />
                  Live
                </>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Unified operational intelligence across your managed services portfolio
          </p>
          {lastUpdated && (
            <p className="text-[10px] text-slate-600 font-mono mt-1">
              Last synced {lastUpdated}
            </p>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isFetching}
          className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white self-start sm:self-auto"
        >
          <RefreshCw className={`size-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Syncing...' : 'Refresh'}
        </Button>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Critical Alert Banner                                             */}
      {/* ----------------------------------------------------------------- */}
      <CriticalAlertBanner count={metrics.criticalAlerts} />

      {/* ----------------------------------------------------------------- */}
      {/* KPI Row                                                           */}
      {/* ----------------------------------------------------------------- */}
      <KpiGrid
        metrics={{
          mrr: metrics.mrr,
          churnRate: metrics.churnRate,
          activeTickets: metrics.activeTickets,
          ticketVelocity: metrics.ticketVelocity,
          slaCompliance: metrics.slaCompliance,
          criticalAlerts: metrics.criticalAlerts,
        }}
      />

      {/* ----------------------------------------------------------------- */}
      {/* Health Score Gauge + Revenue Chart                                */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-slate-900/50 border-slate-800 backdrop-blur-md flex flex-col items-center justify-center">
          <HealthScoreGauge score={metrics.healthScore} />
          <div className="mt-4 flex items-center gap-2">
            <Signal className="size-3.5 text-slate-500" />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">
              Platform Vitals
            </span>
          </div>
        </Card>
        <div className="lg:col-span-3">
          <RevenueChart data={metrics.revenueTrend} />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Security + Backup + Service Health                                */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <SecurityPosture data={metrics.security} />
        <BackupIntelligence data={metrics.backup} />
        <HealthGrid items={metrics.healthGrid} />
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Uptime Heatmap + License Utilization                              */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UptimeHeatmap data={metrics.uptimeHistory} />
        {metrics.licenseUtilization && (
          <LicenseUtilizationBar data={metrics.licenseUtilization} />
        )}
      </div>
    </div>
  )
}
